// The whole ImageBoss URL grammar for EPF's public renderer, extracted from ImageBoss.tsx
// so it can be unit-tested in isolation. Owns every branch: the legacy sizing ops
// (cover/width/height) AND the new crop path (width + rct-* extract). Retina density is
// expressed with native `dpr:N` (verified byte-identical to the old dimension-doubling).
//
// SSOT note: this is a hand-rolled copy of @zencity/ebuilder-types/imageboss
// (buildImageBossUrl). The golden-fixture test in imageBossUrl.spec.ts reproduces that
// package's golden crop URL and is the drift fence between the two implementations.

import { DEFAULT_CROP_KEY, ImageCrop, ImageCropRect, ImageCropSource } from 'types/imageCrop';

// The operation marker QBB emits in base media URLs; ImageBoss ops replace it.
const CDN_MARKER = '/cdn/';
// Retina densities served through native dpr:N instead of doubled dimensions. 1x is the base
// URL (no dpr segment); these add dpr:N. 3x covers modern flagship phones; ImageBoss caps dpr at 3.
const SRCSET_DENSITIES = [2, 3];

interface PixelRect {
  left: number;
  top: number;
  rectWidth: number;
  rectHeight: number;
}

export interface ImageBossImage {
  src: string;
  // Omitted for a native-resolution crop (cdn carrier) — the extract is already max
  // resolution, so there is no higher-density variant to offer.
  srcSet?: string;
}

export interface BuildImageBossUrlParams {
  // Base ImageBoss URL, containing the `/cdn/` operation marker.
  basePath: string;
  // Natural px dimensions the rect was authored against.
  source: ImageCropSource;
  // Normalized crop rect; authoritative for the extract region.
  rect: ImageCropRect;
  // Output width in px (the `width/<W>` operation).
  width: number;
  // Optional density multiplier; ImageBoss accepts 1-3.
  dpr?: number;
}

// Convert a normalized rect to an in-bounds pixel rect. ImageBoss hard-400s on any
// out-of-bounds/negative rect (it does not clamp), so this clamp is mandatory: offsets
// floored at 0 and kept < source; dimensions >= 1 and shrunk so `l+w <= source.width`
// and `t+h <= source.height` after rounding. Identical to the QBB builder.
function clampRectToSource(rect: ImageCropRect, source: ImageCropSource): PixelRect {
  const sourceWidth = source.width;
  const sourceHeight = source.height;

  let left = Math.max(0, Math.round(rect.x * sourceWidth));
  let top = Math.max(0, Math.round(rect.y * sourceHeight));
  left = Math.min(left, sourceWidth - 1);
  top = Math.min(top, sourceHeight - 1);

  let rectWidth = Math.max(1, Math.round(rect.width * sourceWidth));
  let rectHeight = Math.max(1, Math.round(rect.height * sourceHeight));
  rectWidth = Math.min(rectWidth, sourceWidth - left);
  rectHeight = Math.min(rectHeight, sourceHeight - top);

  return { left, top, rectWidth, rectHeight };
}

// The `rct-*[,dpr]` options group, comma-joined. Shared by every crop carrier so the
// extract grammar is defined once.
function cropOptions(rect: ImageCropRect, source: ImageCropSource, dpr?: number): string {
  // Single seam for the future min-height reveal (coverCropForBox, ENG-4102 follow-up) —
  // the effective rect is otherwise the stored rect. Do NOT widen/re-center here for MVP.
  const effectiveRect = rect;
  const { left, top, rectWidth, rectHeight } = clampRectToSource(effectiveRect, source);

  const options = [`rct-l:${left}`, `rct-t:${top}`, `rct-w:${rectWidth}`, `rct-h:${rectHeight}`];
  if (dpr !== undefined) {
    options.push(`dpr:${dpr}`);
  }
  return options.join(',');
}

interface BuildCropUrlParams {
  basePath: string;
  // Carrier op that sets output size: `width/W`, `height/H`, or `cdn` (native resolution).
  operation: string;
  source: ImageCropSource;
  rect: ImageCropRect;
  dpr?: number;
}

// Insert an extract onto a carrier operation: `/<operation>/rct-…[,dpr]/`. The carrier
// controls output size; the rect is what actually crops. Replaces the `/cdn/` marker.
function buildCropUrl({ basePath, operation, source, rect, dpr }: BuildCropUrlParams): string {
  if (!basePath.includes(CDN_MARKER)) {
    throw new Error(`buildImageBossUrl: basePath is missing the "${CDN_MARKER}" operation marker: ${basePath}`);
  }
  return basePath.replace(CDN_MARKER, `/${operation}/${cropOptions(rect, source, dpr)}/`);
}

// Build the ImageBoss crop URL on the `width` carrier: `/width/<W>/rct-…[,dpr:D]/<path>`.
// Mirror of @zencity/ebuilder-types buildImageBossUrl — keep the output byte-for-byte
// identical (the golden-fixture test depends on it).
export function buildImageBossUrl({ basePath, source, rect, width, dpr }: BuildImageBossUrlParams): string {
  return buildCropUrl({ basePath, operation: `width/${width}`, source, rect, dpr });
}

// Append an ImageBoss options segment (e.g. `dpr:2`) after a sizing operation.
// A 1x request omits the segment entirely (dpr defaults to 1).
function buildSizingUrl(url: string, operation: string, dpr?: number): string {
  const segment = dpr === undefined ? `/${operation}/` : `/${operation}/dpr:${dpr}/`;
  return url.replace(CDN_MARKER, segment);
}

// Pick the sizing operation for a non-crop request. Mirrors the legacy branches:
// width+height => cover:center, width-only => width, height-only => height.
function sizingOperation(width?: number, height?: number): string | null {
  if (width && height) return `cover:center/${width}x${height}`;
  if (width) return `width/${width}`;
  if (height) return `height/${height}`;
  return null;
}

// A 1x/2x/3x density descriptor. Each retina tier is the same URL carrying dpr:N, never doubled
// dimensions. `forDensity` renders the URL for a given dpr; 1x is the supplied base URL.
function buildSrcSet(base: string, forDensity: (dpr: number) => string): string {
  const variants = SRCSET_DENSITIES.map((dpr) => `${forDensity(dpr)} ${dpr}x`);
  return [`${base} 1x`, ...variants].join(', ');
}

// Guard against malformed crop metadata reaching URL construction: a NaN/missing/non-numeric
// rect or source produces an out-of-range rct-* value that ImageBoss hard-400s on. An invalid
// crop is treated as "no crop" so the caller falls through to plain sizing (full-frame render),
// keeping a bad payload from breaking the image entirely.
function isRenderableCrop(imageCrop: ImageCrop): boolean {
  const entry = imageCrop.crops?.[DEFAULT_CROP_KEY];
  if (!entry) return false;
  const { source } = imageCrop;
  const { rect } = entry;
  return (
    Number.isFinite(source?.width) &&
    Number.isFinite(source?.height) &&
    source.width > 0 &&
    source.height > 0 &&
    Number.isFinite(rect?.x) &&
    Number.isFinite(rect?.y) &&
    Number.isFinite(rect?.width) &&
    Number.isFinite(rect?.height)
  );
}

// The passthrough carrier: the rect rides on `/cdn/` with no resize, so the extract is
// served at its native resolution. Used when a crop has no target size (e.g. a lightbox).
const CDN_CARRIER = 'cdn';

// Build the crop image. The rect frames the picture; the carrier only sets output size —
// `width/W` when the surface has a width, else `height/H`, else the native-resolution cdn
// passthrough. cover:center is never used for a crop: it would run a second, centered crop
// that fights the builder's authored rect. A width/height carrier gets a 1x/2x dpr srcSet;
// the native cdn carrier is already max quality so it has no retina variant.
function resolveCropImage(url: string, imageCrop: ImageCrop, width?: number, height?: number): ImageBossImage {
  const { source, crops } = imageCrop;
  const { rect } = crops[DEFAULT_CROP_KEY];

  if (width || height) {
    const operation = width ? `width/${width}` : `height/${height}`;
    const src = buildCropUrl({ basePath: url, operation, source, rect });
    const srcSet = buildSrcSet(src, (dpr) => buildCropUrl({ basePath: url, operation, source, rect, dpr }));
    return { src, srcSet };
  }

  return { src: buildCropUrl({ basePath: url, operation: CDN_CARRIER, source, rect }) };
}

export interface ResolveImageBossParams {
  url: string;
  width?: number;
  height?: number;
  imageCrop?: ImageCrop;
}

// Resolve the render-ready image for an ImageBoss-served URL, or null when the URL is not
// an ImageBoss CDN URL or there is nothing to do (no crop and no size — caller renders it
// raw). A valid crop always wins and is honored via cdn even without a size; only when
// there is no crop do we fall back to plain width/height/cover sizing.
export function resolveImageBossImage({
  url,
  width,
  height,
  imageCrop,
}: ResolveImageBossParams): ImageBossImage | null {
  const isImageBoss = url.includes('imageboss') && url.includes(CDN_MARKER);
  if (!isImageBoss) return null;

  if (imageCrop && isRenderableCrop(imageCrop)) {
    return resolveCropImage(url, imageCrop, width, height);
  }

  const operation = sizingOperation(width, height);
  if (!operation) return null;

  const src = buildSizingUrl(url, operation);
  const srcSet = buildSrcSet(src, (dpr) => buildSizingUrl(url, operation, dpr));
  return { src, srcSet };
}
